import React, { useEffect, useState } from 'react';
import {
  SkillFocus,
  SkillFramework,
  skillFrameworkService,
} from '../../../services/skillFrameworkService';
import { CourseDesign, designRunsService } from '../../../services/designRunsService';
import { Card, SmallButton, TextArea, TextField } from './editors';

const CEFR_OPTIONS = ['A1-A2', 'A2-B1', 'B1', 'B1-B2', 'B2', 'B2-C1', 'C1-C2'];

/** The design INPUTS: what we asked the designer for. Editing + "Save & redesign"
    regenerates the whole design from the new brief. */
export function BriefTab({ runId, scope, busy, busyReason, onRedesigned, setError }: {
  runId: string;
  scope: CourseDesign['scope'];
  busy: boolean;
  busyReason?: string;
  onRedesigned: () => void;
  setError: (message: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(scope);
  const [framework, setFramework] = useState<SkillFramework | null>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(scope); }, [scope]);
  useEffect(() => {
    skillFrameworkService.get().then(setFramework).catch(() => setFramework(null));
  }, []);

  const targeting = draft.targeting ?? { cefr_level: '', audience: '' };
  const skillFocus = (targeting.skill_focus ?? []) as SkillFocus[];
  const setTargeting = (patch: Partial<typeof targeting>) =>
    setDraft({ ...draft, targeting: { ...targeting, ...patch } });

  // Send only the fields the brief endpoint owns — the scope also carries
  // design-layer data (objectives, vocab exceptions) with their own edit paths.
  const briefPayload = () => ({
    course_title: draft.course_title,
    course_description: draft.course_description,
    icp: draft.icp,
    directional_guidance: draft.directional_guidance,
    lesson_count_hint: draft.lesson_count_hint,
    lesson_count_mode: draft.lesson_count_mode,
    duration_minutes_hint: draft.duration_minutes_hint,
    test_policy: draft.test_policy,
    targeting: draft.targeting,
  });

  const saveAndRedesign = async () => {
    const confirmed = window.confirm(
      'Save this brief and redesign the course?\n\nThe syllabus and all lessons will be '
      + 'regenerated from the new brief as a new version. In-place edits to the current '
      + 'design will be replaced.',
    );
    if (!confirmed) return;
    setSaving(true);
    setError(null);
    setIssues([]);
    try {
      const result = await designRunsService.putBrief(runId, briefPayload());
      if (result.issues?.length) {
        setIssues(result.issues);
        return;
      }
      await designRunsService.rerunDesign(runId);
      setEditing(false);
      onRedesigned();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const row = (label: string, value: React.ReactNode) => (
    <div className="flex gap-2 text-sm">
      <span className="w-40 shrink-0 text-gray-500">{label}</span>
      <span className="text-gray-800">{value}</span>
    </div>
  );

  if (!editing) {
    return (
      <Card className="space-y-2">
        <div className="flex justify-between items-center mb-1">
          <div className="text-sm font-medium text-gray-900">
            The brief — what the designer was asked for
          </div>
          <span className="flex items-center gap-2">
            {busy && busyReason && <span className="text-xs text-gray-400">{busyReason}</span>}
            <SmallButton disabled={busy} title={busy ? busyReason : undefined}
              onClick={() => setEditing(true)}>
              Edit brief
            </SmallButton>
          </span>
        </div>
        {row('Title', draft.course_title)}
        {row('Description', draft.course_description)}
        {row('Audience', targeting.audience)}
        {row('Level', targeting.cefr_level)}
        {row('Skill focus', skillFocus.map((s) => `${s.category} / ${s.skill}`).join(' · ') || '—')}
        {row('Lessons', `${draft.lesson_count_mode === 'exact' ? 'Exactly' : 'Approximately'} ${draft.lesson_count_hint || '—'}`)}
        {row('Test policy', draft.test_policy === 'none' || !draft.test_policy
          ? 'No graded test' : draft.test_policy)}
        {row('Guidance', draft.directional_guidance || '—')}
      </Card>
    );
  }

  return (
    <Card className="space-y-3">
      <div className="text-sm font-medium text-gray-900">Edit brief</div>
      {issues.length > 0 && (
        <div className="p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          {issues.map((i) => <div key={i}>• {i}</div>)}
        </div>
      )}
      <TextField label="Title" value={draft.course_title}
        onChange={(course_title) => setDraft({ ...draft, course_title })} />
      <TextArea label="Description" rows={3} value={draft.course_description}
        onChange={(course_description) => setDraft({ ...draft, course_description })} />
      <TextField label="Audience" value={targeting.audience}
        onChange={(audience) => setTargeting({ audience })} />
      <div className="grid grid-cols-3 gap-3">
        <label className="block">
          <span className="block text-xs text-gray-500 mb-0.5">Level</span>
          <select value={targeting.cefr_level} onChange={(e) => setTargeting({ cefr_level: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm bg-white">
            {CEFR_OPTIONS.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs text-gray-500 mb-0.5">Lesson count</span>
          <select value={draft.lesson_count_mode ?? 'approx'}
            onChange={(e) => setDraft({ ...draft, lesson_count_mode: e.target.value as 'approx' | 'exact' })}
            className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm bg-white">
            <option value="approx">Approximately</option>
            <option value="exact">Exactly</option>
          </select>
        </label>
        <label className="block">
          <span className="block text-xs text-gray-500 mb-0.5">&nbsp;</span>
          <input type="number" min={1} max={12} value={Number(draft.lesson_count_hint) || 4}
            onChange={(e) => setDraft({ ...draft, lesson_count_hint: Number(e.target.value) })}
            className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm" />
        </label>
      </div>
      <div>
        <span className="block text-xs text-gray-500 mb-0.5">Skill focus</span>
        {skillFocus.map((sf, i) => (
          <div key={i} className="flex gap-2 mb-1">
            <select value={sf.category}
              onChange={(e) => {
                const category = e.target.value;
                const next = [...skillFocus];
                next[i] = { category, skill: framework?.categories.find((c) => c.category === category)?.skills[0]?.skill ?? '' };
                setTargeting({ skill_focus: next });
              }}
              className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white">
              {(framework?.categories ?? []).map((c) => <option key={c.category}>{c.category}</option>)}
            </select>
            <select value={sf.skill}
              onChange={(e) => {
                const next = [...skillFocus];
                next[i] = { ...next[i], skill: e.target.value };
                setTargeting({ skill_focus: next });
              }}
              className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white">
              {(framework?.categories.find((c) => c.category === sf.category)?.skills ?? [])
                .map((s) => <option key={s.skill}>{s.skill}</option>)}
            </select>
            <button className="text-xs text-red-500"
              onClick={() => setTargeting({ skill_focus: skillFocus.filter((_, j) => j !== i) })}>✕</button>
          </div>
        ))}
        <button className="text-xs text-blue-600"
          onClick={() => {
            const first = framework?.categories[0];
            setTargeting({
              skill_focus: [...skillFocus,
                { category: first?.category ?? '', skill: first?.skills[0]?.skill ?? '' }],
            });
          }}>
          + skill
        </button>
      </div>
      <label className="block">
        <span className="block text-xs text-gray-500 mb-0.5">Test policy</span>
        <select value={draft.test_policy ?? 'none'}
          onChange={(e) => setDraft({ ...draft, test_policy: e.target.value })}
          className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm bg-white">
          <option value="none">No graded test</option>
          <option value="end_of_course" disabled>End-of-course graded test (not yet supported)</option>
          <option value="both" disabled>Per-lesson + end-of-course (not yet supported)</option>
        </select>
      </label>
      <TextArea label="Guidance for the designer" rows={2} value={draft.directional_guidance ?? ''}
        onChange={(directional_guidance) => setDraft({ ...draft, directional_guidance })} />
      <div className="flex gap-2">
        <SmallButton tone="primary" disabled={saving} onClick={saveAndRedesign}>
          {saving ? 'Saving…' : 'Save brief & redesign'}
        </SmallButton>
        <SmallButton disabled={saving} onClick={() => { setEditing(false); setDraft(scope); setIssues([]); }}>
          Cancel
        </SmallButton>
      </div>
    </Card>
  );
}
